// Shared trigger convention for every in-app tour: `#cpm-tour=<slug>` in the URL (used by
// Messenger release announcements — see Modules/Bubble/config/release-announcements.php —
// and by this drawer). On load and on hashchange we dispatch `cpm:tour:<slug>` as a window
// CustomEvent; hand-authored tours (phi-safe-tour.js, cpm-retirement-tour.js) already listen
// for their own exact event name.
//
// Tours defined in config/tours.php that have no hand-authored file of their own fall back to
// this generic Shepherd launcher, styled with the one shared `.nurse-performance-tour` skin. Content authors register real steps per tour id via
// registerTourSteps(); an unregistered id gets a single generic "Welcome" step as a placeholder.
const registeredSteps = {}

// Ids that already have their own dedicated file/listener — do not also run them through the
// generic wrapper below, or two tours would open at once for the same hash.
const HAND_AUTHORED_TOUR_IDS = ['phi-redacted', 'old-cpm-retirement', 'nurse_my_performance_progress', 'nurse_ivr_withdrawal', 'nurse_patient_list_caseload', 'two-factor-setup', 'report-a-bug']

export function registerTourSteps(tourId, steps) {
    registeredSteps[tourId] = steps
}

function tourButton(action, text, classes = '') {
    return {action, classes, text}
}

// A tour that spans pages (chart, then the call popup) leaves a short-lived note in localStorage,
// which every same-origin window shares; the next page load resumes it if it can show a step.
const PENDING_KEY = 'cpm.tour.pending'
const PENDING_MAX_AGE_MS = 20 * 60 * 1000

export function setPendingTour(tourId, browserWindow = window) {
    try { browserWindow.localStorage.setItem(PENDING_KEY, JSON.stringify({id: tourId, at: Date.now()})) } catch { /* storage blocked: the tour just won't resume */ }
}

export function clearPendingTour(browserWindow = window) {
    try { browserWindow.localStorage.removeItem(PENDING_KEY) } catch { /* nothing to clear */ }
}

function pendingTourId(browserWindow) {
    try {
        const pending = JSON.parse(browserWindow.localStorage.getItem(PENDING_KEY))

        return pending && Date.now() - pending.at < PENDING_MAX_AGE_MS ? pending.id : null
    } catch {
        return null
    }
}

export function postJson(browserWindow, url) {
    const token = browserWindow.document.querySelector('meta[name="csrf-token"]')?.content

    return browserWindow.fetch(url, {
        method: 'POST',
        headers: {'X-CSRF-TOKEN': token, 'X-Requested-With': 'XMLHttpRequest'},
    }).then(response => {
        const progress = url.match(/^\/tours\/([^/]+)\/(start|complete|dismiss)$/)

        if (response.ok && progress) {
            browserWindow.dispatchEvent(new CustomEvent('cpm:tour-progress', {
                detail: {
                    id: decodeURIComponent(progress[1]),
                    status: {start: 'started', complete: 'completed', dismiss: 'dismissed'}[progress[2]],
                },
            }))
        }

        return response
    })
}

// Progress dots + "Step N of Total" label shared by every hand-authored tour so they all match
// the nurse-performance-tour skin exactly (see resources/css/product-tours.css).
export function buildTourProgressElement(index, total, documentObject = document) {
    const progress = documentObject.createElement('div')
    progress.className = 'nurse-performance-tour-progress'
    progress.setAttribute('role', 'progressbar')
    progress.setAttribute('aria-label', 'Tour progress')
    progress.setAttribute('aria-valuemin', '1')
    progress.setAttribute('aria-valuemax', String(total))
    progress.setAttribute('aria-valuenow', String(index + 1))

    const progressLabel = documentObject.createElement('span')
    progressLabel.className = 'nurse-performance-tour-progress-label'
    progressLabel.textContent = `Step ${index + 1} of ${total}`
    progress.append(progressLabel)

    const progressTrack = documentObject.createElement('span')
    progressTrack.className = 'nurse-performance-tour-progress-track'
    progressTrack.setAttribute('aria-hidden', 'true')

    Array.from({length: total}, (_, segmentIndex) => {
        const progressSegment = documentObject.createElement('span')
        const state = segmentIndex < index
            ? 'is-complete'
            : (segmentIndex === index ? 'is-current' : '')

        progressSegment.className = `nurse-performance-tour-progress-segment ${state}`
        progressTrack.append(progressSegment)
    })

    progress.append(progressTrack)

    return progress
}

// Step body shared by every tour: progress label + bar, then the plain-text copy.
export function buildTourStepContent(index, total, text, documentObject = document) {
    const content = documentObject.createElement('div')
    const body = documentObject.createElement('p')
    body.className = 'nurse-performance-tour-body'
    body.textContent = text
    content.append(buildTourProgressElement(index, total, documentObject), body)

    return content
}

function tourSlugFromHash(browserWindow) {
    const match = browserWindow.location.hash.match(/cpm-tour=([^&]+)/)

    return match ? decodeURIComponent(match[1]) : null
}

// A registered entry is either an array of steps or a function `(documentObject, browserWindow)`
// returning `{steps, onEnd}`; the function form lets a tour inspect the page when it starts.
// A step is `{selector, position, title, text, before, advanceOn}`: `advanceOn` (`{selector, event}`) makes the step wait for that real click instead of a Next button, and `waitFor(done)` (returns a cleanup function) waits for any other gesture and calls `done` when it happens; `selector` may be null for a centred
// step, and `before` runs (and may return a promise) just before the step is shown, for tours
// that must open a menu or unfold a panel first. `onEnd` runs when the tour completes or is dismissed.
function resolveTour(browserWindow, documentObject, tourId) {
    const entry = registeredSteps[tourId] ?? [
        {selector: 'h1, h2', position: 'bottom', title: 'Welcome', text: 'This tour has no steps configured yet.'},
    ]

    return typeof entry === 'function' ? entry(documentObject, browserWindow) : {steps: entry, onEnd: null}
}

export function startRegistryTour(browserWindow, documentObject, ShepherdLibrary, tourId) {
    const {steps, onEnd, deferred = false, onDefer, onCancel} = resolveTour(browserWindow, documentObject, tourId)

    // A resumed tour with nothing to show on this page stays quiet.
    if (steps.length === 0) {
        return
    }

    onDefer?.()

    const tour = new ShepherdLibrary.Tour({
        defaultStepOptions: {
            cancelIcon: {enabled: true},
            classes: 'nurse-performance-tour',
            modalOverlayOpeningPadding: 10,
            modalOverlayOpeningRadius: 18,
            scrollTo: {behavior: 'smooth', block: 'center'},
        },
        exitOnEsc: true,
        exitOnOverlayClick: true,
        useModalOverlay: true,
    })

    let stopWaiting = null

    steps.forEach((step, index) => {
        const isFirst = index === 0
        const isLast = index === steps.length - 1
        const buttons = []

        if (step.advanceOn || step.waitFor) {
            // The step asks for a real action: the page's own handler does the work and the tour moves on, so no Next button.
            if (!isFirst) buttons.push(tourButton(() => tour.back(), 'Back', 'shepherd-button-secondary'))
            // A gesture such as a drag cannot be assumed to work for everyone, so it can be skipped.
            if (step.waitFor) buttons.push(tourButton(() => tour.next(), 'Skip', 'shepherd-button-secondary'))
        } else if (deferred) {
            buttons.push(tourButton(() => tour.complete(), 'Got it'))
        } else {
            buttons.push(isFirst
                ? tourButton(() => tour.cancel(), 'Not now', 'shepherd-button-secondary')
                : tourButton(() => tour.back(), 'Back', 'shepherd-button-secondary'))
            buttons.push(tourButton(
                () => isLast ? tour.complete() : tour.next(),
                isLast ? 'Done' : (isFirst ? 'Start tour' : 'Next'),
            ))
        }

        tour.addStep({
            id: `${tourId}-${index}`,
            advanceOn: step.advanceOn,
            when: step.waitFor ? {
                show: () => { stopWaiting = step.waitFor(() => tour.next()) },
                hide: () => { stopWaiting?.(); stopWaiting = null },
            } : undefined,
            attachTo: step.selector ? {element: () => documentObject.querySelector(step.selector), on: step.position} : undefined,
            beforeShowPromise: step.before ? () => Promise.resolve(step.before()) : undefined,
            buttons,
            title: step.title,
            text: buildTourStepContent(index, steps.length, step.text, documentObject),
        })
    })

    tour.on('complete', () => {
        onEnd?.()
        if (!deferred) postJson(browserWindow, `/tours/${tourId}/complete`)
    })
    tour.on('cancel', () => {
        onEnd?.()
        onCancel?.()
        if (!deferred) postJson(browserWindow, `/tours/${tourId}/dismiss`)
    })

    if (deferred) tour.start()
    else postJson(browserWindow, `/tours/${tourId}/start`).finally(() => tour.start())
}

export function initProductTours(browserWindow, documentObject, ShepherdLibrary) {
    // Lets a page button (e.g. "Take the tour") start a registry tour without touching the URL hash.
    browserWindow.cpmStartTour = tourId => startRegistryTour(browserWindow, documentObject, ShepherdLibrary, tourId)

    const handleHash = () => {
        const slug = tourSlugFromHash(browserWindow)

        if (!slug) {
            return
        }

        browserWindow.dispatchEvent(new CustomEvent(`cpm:tour:${slug}`))

        if (HAND_AUTHORED_TOUR_IDS.includes(slug)) {
            postJson(browserWindow, `/tours/${slug}/start`)
        } else {
            startRegistryTour(browserWindow, documentObject, ShepherdLibrary, slug)
        }
    }

    handleHash()

    const resumeId = tourSlugFromHash(browserWindow) ? null : pendingTourId(browserWindow)

    if (resumeId && !HAND_AUTHORED_TOUR_IDS.includes(resumeId)) {
        startRegistryTour(browserWindow, documentObject, ShepherdLibrary, resumeId)
    }

    browserWindow.addEventListener('hashchange', handleHash)
}
